// src/pages/ParentForm.js
//
// Outil admin pour lier un compte parent (app) à ses enfants.
//
// id (URL param) = Firebase UID du document parents/{uid}
//   → ce UID est créé quand le parent s'inscrit sur l'app.
//
// Trois sources pour trouver les enfants à lier :
//   1. "Inscrits site"  : registrations/ filtrées par email du parent
//   2. "Demandes app"   : parents/{uid}.linkRequests[] (depuis mon_enfant_page.dart)
//   3. "Lier manuellement" : recherche libre dans users/ (role Élève / Élève adulte)
//
// Action de liaison : écrit { uid, firstName, lastName } dans parents/{uid}.children[]
//
// ⚠️ uid ne doit JAMAIS être vide : côté app, parent_dashboard.dart filtre les
// enfants avec uid vide (ils ne peuvent de toute façon rien afficher, aucune
// donnée n'étant associée à un uid vide). Pour un enfant sans téléphone/sans
// app, on crée donc un compte élève "fantôme" : un document users/{id} normal
// (role Élève), sans compte Firebase Auth associé — personne ne se connecte
// avec, mais son uid est un vrai identifiant Firestore utilisable partout
// (présences, ressources, agenda...) via le compte du parent.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  doc, getDoc, updateDoc,
  collection, getDocs, query, where
} from 'firebase/firestore';
import {
  Box, Typography, TextField, Button, Grid, Paper,
  Tabs, Tab, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, Alert,
  InputAdornment, List, ListItem, ListItemText,
  CircularProgress, Divider, Badge
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Check as CheckIcon,
  HourglassTop as PendingIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const PURPLE = '#7C3AED';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const avatar = (name, bg = '#EDE9FE', color = PURPLE) => (
  <Avatar sx={{ bgcolor: bg, color, fontWeight: 700, width: 36, height: 36 }}>
    {(name || '?')[0].toUpperCase()}
  </Avatar>
);

// ─── Composant principal ───────────────────────────────────────────────────────

export default function ParentForm() {
  const { id } = useParams(); // Firebase UID du parent dans parents/
  const navigate = useNavigate();

  // ── État données ──────────────────────────────────────────────────
  const [loading, setLoading]                       = useState(true);
  const [saving, setSaving]                         = useState(false);
  const [parentDoc, setParentDoc]                   = useState(null);
  const [registrationChildren, setRegistrationChildren] = useState([]);
  const [linkedChildren, setLinkedChildren]         = useState([]);
  const [pendingRequests, setPendingRequests]        = useState([]);
  const [allStudents, setAllStudents]               = useState([]);

  // ── Dialog recherche élève ────────────────────────────────────────
  const [searchDialog, setSearchDialog] = useState({ open: false, target: null });
  const [studentSearch, setStudentSearch] = useState('');

  // ── Dialog correction nom d'un enfant lié ──────────────────────────
  const [editChildDialog, setEditChildDialog] = useState({
    open: false, index: null, firstName: '', lastName: '',
  });

  // ── Onglet actif ──────────────────────────────────────────────────
  const [tab, setTab] = useState(0);

  // ── Chargement ────────────────────────────────────────────────────

  useEffect(() => { if (id) load(); else setLoading(false); }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      // 1. Compte app du parent
      const snap = await getDoc(doc(db, 'parents', id));
      if (!snap.exists()) { setLoading(false); return; }
      const data = { id: snap.id, ...snap.data() };
      setParentDoc(data);
      setLinkedChildren(data.children ?? []);
      setPendingRequests(data.linkRequests ?? []);

      // 2. Inscriptions site web correspondant à l'email du parent
      const email = data.email;
      if (email) {
        const regs = await getDocs(
          query(collection(db, 'registrations'),
            where('contactInfo.email', '==', email))
        );
        const seen = new Set();
        const children = [];
        for (const r of regs.docs) {
          const d = r.data();
          for (const p of (d.persons ?? [])) {
            if (!p.dateOfBirth) continue; // adulte, pas un enfant
            const key = `${p.firstName}-${p.lastName}-${p.dateOfBirth}`.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            children.push({
              key,
              firstName:   p.firstName  ?? '',
              lastName:    p.lastName   ?? '',
              dateOfBirth: p.dateOfBirth ?? '',
              course:      d.courseTitle ?? '',
              plan:        d.planName   ?? '',
            });
          }
        }
        setRegistrationChildren(children);
      }

      // 3. Tous les comptes élèves pour la recherche
      const usersSnap = await getDocs(collection(db, 'users'));
      setAllStudents(
        usersSnap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(u => ['Élève', 'Élève adulte', 'eleve', 'eleve_adult'].includes(u.role))
      );
    } catch (err) {
      console.error('ParentForm.load:', err);
    }
    setLoading(false);
  };

  // ── Filtrage élèves dans le dialog ────────────────────────────────

  const filteredStudents = studentSearch
    ? allStudents.filter(s => {
        const q = studentSearch.toLowerCase();
        return (s.firstName ?? '').toLowerCase().includes(q)
          || (s.lastName  ?? '').toLowerCase().includes(q)
          || (s.pseudo    ?? '').toLowerCase().includes(q)
          || (s.email     ?? '').toLowerCase().includes(q);
      })
    : allStudents;

  // ── Création d'un compte élève "sans app" (pas de tél/email) ────────
  // Redirige vers le formulaire "Ajouter utilisateur" (UserForm.js),
  // préempli avec le nom de l'enfant. UserForm.js sait déjà créer un
  // compte sans email/mot de passe (uid généré localement, pas de compte
  // Firebase Auth) ET relier automatiquement ce nouvel uid à une demande
  // en attente ou à une entrée children[] existante avec le même nom mais
  // un uid vide (voir autoLinkToParent dans UserForm.js) — donc pas besoin
  // de dupliquer cette logique ici : un seul endroit qui crée des comptes
  // élève, plutôt que deux versions qui risquent de diverger.
  const goCreateStudentAccount = (childInfo) => {
    const params = new URLSearchParams({
      firstName: childInfo.firstName || '',
      lastName:  childInfo.lastName  || '',
      role:      'Élève',
    });
    navigate(`/users/new?${params.toString()}`);
  };

  // ── Liaison enfant ────────────────────────────────────────────────

  const linkChild = async (childInfo, student) => {
    // student.uid doit toujours être un uid réel désormais (soit un compte
    // élève existant, soit un compte "fantôme" tout juste créé) — plus de
    // liaison avec uid vide, qui rendait l'enfant invisible partout dans
    // l'app malgré la demande marquée "Approuvée".
    const entry = {
      uid:       student.uid,
      firstName: childInfo.firstName,
      lastName:  childInfo.lastName,
    };
    setSaving(true);
    try {
      const newChildren = [...linkedChildren, entry];
      const updates = { children: newChildren };

      // Si la demande vient de l'app, la marquer approuvée
      if (childInfo.requestIndex !== undefined) {
        const newReqs = pendingRequests.map((r, i) =>
          i === childInfo.requestIndex ? { ...r, status: 'approved' } : r
        );
        updates.linkRequests = newReqs;
        setPendingRequests(newReqs);
      }

      await updateDoc(doc(db, 'parents', id), updates);
      setLinkedChildren(newChildren);
      closeSearch();
    } catch (err) {
      console.error('linkChild:', err);
    }
    setSaving(false);
  };

  // ── Déliaison ─────────────────────────────────────────────────────

  const unlinkChild = async (index) => {
    if (!window.confirm('Délier cet enfant du compte parent ?')) return;
    const newChildren = linkedChildren.filter((_, i) => i !== index);
    await updateDoc(doc(db, 'parents', id), { children: newChildren });
    setLinkedChildren(newChildren);
  };

  // ── Correction du nom d'un enfant lié (erreur de saisie) ────────────

  const openEditChild = (index) => {
    const child = linkedChildren[index];
    setEditChildDialog({
      open: true, index,
      firstName: child.firstName || '',
      lastName:  child.lastName  || '',
    });
  };

  const closeEditChild = () =>
    setEditChildDialog({ open: false, index: null, firstName: '', lastName: '' });

  const saveEditChild = async () => {
    const { index, firstName, lastName } = editChildDialog;
    if (index === null) return;
    const newChildren = linkedChildren.map((c, i) =>
      i === index ? { ...c, firstName: firstName.trim(), lastName: lastName.trim() } : c
    );
    await updateDoc(doc(db, 'parents', id), { children: newChildren });
    setLinkedChildren(newChildren);
    closeEditChild();
  };

  // ── Suppression d'une demande erronée ou en double ──────────────────

  const deleteRequest = async (index) => {
    if (!window.confirm('Supprimer cette demande ? Cette action est irréversible.')) return;
    const newReqs = pendingRequests.filter((_, i) => i !== index);
    await updateDoc(doc(db, 'parents', id), { linkRequests: newReqs });
    setPendingRequests(newReqs);
  };

  // ── Helpers dialog ────────────────────────────────────────────────

  const openSearch  = (target) => { setStudentSearch(''); setSearchDialog({ open: true, target }); };
  const closeSearch = ()       => setSearchDialog({ open: false, target: null });

  const alreadyLinked = (first, last) =>
    linkedChildren.some(c =>
      c.firstName.toLowerCase() === first.toLowerCase() &&
      c.lastName.toLowerCase()  === last.toLowerCase()
    );

  const pendingCount = pendingRequests.filter(r => r.status === 'pending').length;

  // ── Render ────────────────────────────────────────────────────────

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: PURPLE }} />
    </Box>
  );

  if (!parentDoc) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Aucun compte app trouvé pour cet identifiant. Ce parent n'a peut-être pas encore
        créé de compte sur l'app MKD Formation.
      </Alert>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/parents')}>
        Retour aux parents
      </Button>
    </Box>
  );

  const parentName = [
    parentDoc.parentFirstName ?? parentDoc.firstName ?? '',
    parentDoc.parentLastName  ?? parentDoc.lastName  ?? '',
  ].join(' ').trim() || 'Parent';

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>

      {/* ── En-tête ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/parents')}><BackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>{parentName}</Typography>
          <Typography variant="body2" color="text.secondary">{parentDoc.email}</Typography>
        </Box>
        <Chip label="Compte app actif" color="success" size="small" />
      </Box>

      {/* ── Onglets ── */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        TabIndicatorProps={{ style: { backgroundColor: PURPLE } }}
      >
        <Tab label="Infos parent" />
        <Tab label={`Inscrits site (${registrationChildren.length})`} />
        <Tab
          label={
            <Badge badgeContent={pendingCount} color="error" sx={{ pr: pendingCount ? 1.5 : 0 }}>
              Demandes app
            </Badge>
          }
        />
        <Tab label={`Liés à l'app (${linkedChildren.length})`} />
      </Tabs>

      {/* ── Onglet 0 : Infos parent ── */}
      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {[
              { label: 'Prénom', val: parentDoc.parentFirstName ?? parentDoc.firstName },
              { label: 'Nom',    val: parentDoc.parentLastName  ?? parentDoc.lastName  },
              { label: 'Email',  val: parentDoc.email },
              { label: 'Tél',    val: parentDoc.phone },
              { label: 'Pseudo', val: parentDoc.pseudo },
              { label: 'UID Firebase', val: id, mono: true },
            ].map(({ label, val, mono }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography fontFamily={mono ? 'monospace' : undefined} fontSize={mono ? 12 : undefined}>
                  {val || '—'}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── Onglet 1 : Enfants inscrits sur le site ── */}
      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enfants trouvés dans les inscriptions du site web pour{' '}
            <strong>{parentDoc.email}</strong>. Cliquez sur "Lier" pour les associer à un compte
            élève de l'app.
          </Alert>

          {registrationChildren.length === 0 ? (
            <Typography color="text.secondary">
              Aucun enfant dans les inscriptions pour cet email.
            </Typography>
          ) : (
            registrationChildren.map((child, i) => {
              const done = alreadyLinked(child.firstName, child.lastName);
              return (
                <Paper key={i} sx={{ p: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {avatar(child.firstName)}
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{child.firstName} {child.lastName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[child.dateOfBirth && `né·e le ${child.dateOfBirth}`,
                        child.course, child.plan].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {done
                    ? <Chip icon={<CheckIcon />} label="Déjà lié" color="success" size="small" />
                    : <Button variant="contained" size="small" startIcon={<LinkIcon />}
                        onClick={() => openSearch(child)}
                        sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}>
                        Lier
                      </Button>
                  }
                </Paper>
              );
            })
          )}
        </Box>
      )}

      {/* ── Onglet 2 : Demandes depuis l'app ── */}
      {tab === 2 && (
        <Box>
          {pendingRequests.length === 0 ? (
            <Typography color="text.secondary">
              Aucune demande reçue depuis l'app pour ce parent.
            </Typography>
          ) : (
            pendingRequests.map((req, i) => {
              const done    = alreadyLinked(req.firstName, req.lastName);
              const pending = req.status === 'pending';
              const borderColor = pending ? '#EF4444' : done ? '#10B981' : '#9CA3AF';
              return (
                <Paper key={i} sx={{
                  p: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2,
                  borderLeft: `4px solid ${borderColor}`,
                }}>
                  {avatar(req.firstName ?? '?', pending ? '#FEF2F2' : '#F0FDF4', pending ? '#EF4444' : '#10B981')}
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{req.firstName} {req.lastName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[req.dateOfBirth && `né·e le ${req.dateOfBirth}`, req.course].filter(Boolean).join(' · ')}
                    </Typography>
                    <Chip
                      size="small"
                      sx={{ mt: 0.5 }}
                      icon={pending ? <PendingIcon /> : <CheckIcon />}
                      label={pending ? 'En attente' : req.status === 'approved' ? 'Approuvée' : 'Refusée'}
                      color={pending ? 'warning' : req.status === 'approved' ? 'success' : 'error'}
                    />
                  </Box>
                  {pending && !done && (
                    <Button variant="contained" size="small" startIcon={<LinkIcon />}
                      onClick={() => openSearch({ ...req, requestIndex: i })}
                      sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}>
                      Lier
                    </Button>
                  )}
                  <IconButton size="small" color="error" onClick={() => deleteRequest(i)} title="Supprimer la demande">
                    <UnlinkIcon fontSize="small" />
                  </IconButton>
                </Paper>
              );
            })
          )}
        </Box>
      )}

      {/* ── Onglet 3 : Enfants liés à l'app ── */}
      {tab === 3 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ces enfants sont visibles dans l'app depuis le compte parent.
          </Alert>

          {linkedChildren.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Aucun enfant lié. Utilisez les onglets "Inscrits site" ou "Demandes app".
            </Typography>
          ) : (
            linkedChildren.map((child, i) => (
              <Paper key={i} sx={{ p: 2, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                {avatar(child.firstName)}
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600}>{child.firstName} {child.lastName}</Typography>
                  {child.uid
                    ? <Typography variant="body2" color="success.main">
                        UID actif : <code style={{ fontSize: 11 }}>{child.uid}</code>
                      </Typography>
                    : <Typography variant="body2" color="error">
                        ⚠️ Aucun compte associé — invisible dans l'app pour le parent
                      </Typography>
                  }
                </Box>
                {!child.uid && (
                  <Button size="small" variant="contained"
                    onClick={() => goCreateStudentAccount(child)}
                    sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}>
                    Créer le compte
                  </Button>
                )}
                <IconButton size="small" onClick={() => openEditChild(i)} title="Corriger le nom">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => unlinkChild(i)} title="Délier">
                  <UnlinkIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))
          )}

          <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => openSearch(null)}
            sx={{ mt: 1, borderColor: PURPLE, color: PURPLE }}>
            Lier un enfant manuellement
          </Button>
        </Box>
      )}

      {/* ── Dialog : corriger le prénom/nom d'un enfant lié ── */}
      <Dialog open={editChildDialog.open} onClose={closeEditChild} maxWidth="xs" fullWidth>
        <DialogTitle>Corriger le nom</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth autoFocus label="Prénom" sx={{ mt: 1, mb: 2 }}
            value={editChildDialog.firstName}
            onChange={e => setEditChildDialog(d => ({ ...d, firstName: e.target.value }))}
          />
          <TextField
            fullWidth label="Nom"
            value={editChildDialog.lastName}
            onChange={e => setEditChildDialog(d => ({ ...d, lastName: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditChild}>Annuler</Button>
          <Button variant="contained" onClick={saveEditChild}
            sx={{ bgcolor: PURPLE, '&:hover': { bgcolor: '#6D28D9' } }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog : recherche du compte élève ── */}
      <Dialog open={searchDialog.open} onClose={closeSearch} maxWidth="sm" fullWidth>
        <DialogTitle>
          {searchDialog.target
            ? `Lier ${searchDialog.target.firstName} ${searchDialog.target.lastName} à un compte élève`
            : 'Sélectionner un compte élève'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth autoFocus
            placeholder="Recherche par prénom, nom, pseudo…"
            value={studentSearch}
            onChange={e => setStudentSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ mt: 1, mb: 1.5 }}
          />

          <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
            {filteredStudents.length === 0
              ? <ListItem><ListItemText secondary="Aucun élève trouvé" /></ListItem>
              : filteredStudents.map(s => (
                  <ListItem
                    key={s.uid}
                    button
                    onClick={() => linkChild(
                      searchDialog.target ?? { firstName: s.firstName ?? '', lastName: s.lastName ?? '' },
                      s
                    )}
                    sx={{ borderRadius: 1, '&:hover': { bgcolor: '#F5F3FF' } }}
                  >
                    {avatar(s.firstName ?? '?')}
                    <ListItemText
                      sx={{ ml: 1.5 }}
                      primary={`${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || s.pseudo}
                      secondary={[s.pseudo && `@${s.pseudo}`, s.email, s.role].filter(Boolean).join(' · ')}
                    />
                  </ListItem>
                ))
            }
          </List>

          <Divider sx={{ my: 1.5 }} />
          <Button fullWidth variant="outlined"
            onClick={() => goCreateStudentAccount(
              searchDialog.target ?? { firstName: '', lastName: '' }
            )}
            sx={{ borderColor: PURPLE, color: PURPLE }}>
            Créer un compte élève (pas de téléphone / pas d'app)
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Ouvre "Ajouter utilisateur" prérempli. Laisser email/mot de passe
            vides crée une fiche élève normale (présences, ressources,
            agenda) sans compte de connexion — la liaison à ce parent se
            fait automatiquement.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSearch}>Annuler</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}